/**
 * CertificationsPage — CRUD cho bảng certifications (Sprint 5).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm, Controller } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListCertificationsCrudQuery,
  useCreateCertificationMutation,
  useUpdateCertificationMutation,
  useDeleteCertificationMutation,
} from '@/store/api/refsCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'
import type { Certification } from '@/types/api'

interface CertFormValues {
  certification: string
  premium: string
  si: string
  sd: string
  print_bonus: boolean
}

const EMPTY: CertFormValues = {
  certification: '',
  premium: '',
  si: '',
  sd: '',
  print_bonus: false,
}

export function CertificationsPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<Certification | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Certification | null>(null)

  const { data, isLoading, error, isFetching } = useListCertificationsCrudQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createCertification, { isLoading: creating }] = useCreateCertificationMutation()
  const [updateCertification, { isLoading: updating }] = useUpdateCertificationMutation()
  const [deleteCertification, { isLoading: deleting }] = useDeleteCertificationMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset, control } = useForm<CertFormValues>({
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        certification: editing.certification,
        premium: editing.premium != null ? String(editing.premium) : '',
        si: editing.si ?? '',
        sd: editing.sd ?? '',
        print_bonus: false,
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.certification, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'certification', headerName: 'Certification', width: 200 },
      {
        field: 'premium',
        headerName: 'Premium',
        width: 130,
        valueFormatter: (value) => formatNumber(value as number | null),
      },
      { field: 'si', headerName: 'SI', width: 120 },
      { field: 'sd', headerName: 'SD', width: 120 },
      {
        field: '_actions',
        headerName: 'Thao tác',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Sửa">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as Certification) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Xoá">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as Certification) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Certification = {
      certification: values.certification.trim(),
      premium: values.premium !== '' ? Number(values.premium) : null,
      si: values.si || null,
      sd: values.sd || null,
    }
    try {
      if (isEdit && editing) {
        await updateCertification({ id: editing.certification, body: payload }).unwrap()
        snack.success(`Đã cập nhật certification "${editing.certification}"`)
      } else {
        await createCertification(payload).unwrap()
        snack.success(`Đã tạo certification "${payload.certification}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteCertification(confirmDelete.certification).unwrap()
      snack.success(`Đã xoá "${confirmDelete.certification}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Xoá thất bại'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Certifications</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          Thêm Certification
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Tìm kiếm"
          placeholder="Certification..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Certification)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Sửa Certification' : 'Thêm Certification'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Certification *" size="small" fullWidth {...register('certification')} disabled={isEdit} />
          <TextField label="Premium" size="small" fullWidth type="number" {...register('premium')} />
          <TextField label="SI" size="small" fullWidth {...register('si')} />
          <TextField label="SD" size="small" fullWidth {...register('sd')} />
          <Controller
            name="print_bonus"
            control={control}
            render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Print Bonus" />
            )}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Xoá Certification"
        message={confirmDelete ? `Xác nhận xoá "${confirmDelete.certification}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default CertificationsPage
